import type { ComponentPropsWithRef, ReactNode } from 'react'

export type NoticeTone = 'neutral' | 'info' | 'warn' | 'error'

interface NoticeProps extends ComponentPropsWithRef<'div'> {
  tone?: NoticeTone
  children: ReactNode
}

/**
 * An inline message.
 *
 * An error notice is a live region, so a failure announced after the fact --
 * a rejected sign-in, an expired code -- reaches a screen reader without the
 * user having to go looking for it. `assertive` for errors, `polite` for the
 * rest, because interrupting someone mid-sentence is only justified when the
 * thing they were doing did not work.
 */
export function Notice({ tone = 'neutral', children, className, ...rest }: NoticeProps) {
  const classes = ['notice', tone !== 'neutral' && `notice--${tone}`, className]
    .filter(Boolean).join(' ')

  return (
    <div
      {...rest}
      className={classes}
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
    >
      {children}
    </div>
  )
}
